'use client';

import { useMemo } from 'react';

import { InfiniteTable, Meter } from '@web/ui';
import { formatHours } from '@web/utils';

import { clampPercentage, formatHoursWithSuffix } from './formatters';
import styles from './JobDetailsPerformanceView.module.scss';

import type {
  JobPerformanceMetrics,
  JobPerformanceServiceTypeRow,
} from '@/features/jobs/types';

type ServiceTypeBreakdownSectionProps = {
  rows: JobPerformanceServiceTypeRow[];
  overview: JobPerformanceMetrics;
};

type ServiceTypeTableRow =
  | {
      id: string;
      kind: 'service_type';
      row: JobPerformanceServiceTypeRow;
    }
  | {
      id: 'totals';
      kind: 'totals';
      row: JobPerformanceMetrics;
    };

export function ServiceTypeBreakdownSection({
  rows,
  overview,
}: ServiceTypeBreakdownSectionProps) {
  const tableRows = useMemo<ServiceTypeTableRow[]>(
    () => [
      ...rows.map((row) => ({
        id: row.service_type.id,
        kind: 'service_type' as const,
        row,
      })),
      {
        id: 'totals' as const,
        kind: 'totals' as const,
        row: overview,
      },
    ],
    [overview, rows],
  );

  const columns = useMemo(
    () => [
      {
        id: 'service_type',
        header: 'SERVICE TYPE',
        accessor: (item: ServiceTypeTableRow) =>
          item.kind === 'totals' ? 'Totals' : item.row.service_type.label,
        sortable: true,
        minWidth: 180,
        cell: (item: ServiceTypeTableRow) =>
          item.kind === 'totals' ? (
            <span className={styles.totalsLabel}>Totals</span>
          ) : (
            <span className={styles.entityName}>
              {item.row.service_type.label}
            </span>
          ),
      },
      {
        id: 'planned',
        header: 'PLANNED',
        accessor: (item: ServiceTypeTableRow) => item.row.current_plan_hours,
        sortable: true,
        minWidth: 90,
        cell: (item: ServiceTypeTableRow) =>
          formatHoursWithSuffix(item.row.current_plan_hours),
      },
      {
        id: 'actual',
        header: 'ACTUAL',
        accessor: (item: ServiceTypeTableRow) => item.row.actual_hours,
        sortable: true,
        minWidth: 90,
        cell: (item: ServiceTypeTableRow) =>
          formatHoursWithSuffix(item.row.actual_hours),
      },
      {
        id: 'meter',
        header: 'METER',
        accessor: (item: ServiceTypeTableRow) => item.row.actual_pct,
        sortable: true,
        minWidth: 170,
        cell: (item: ServiceTypeTableRow) => (
          <Meter
            percentage={clampPercentage(item.row.actual_pct)}
            label={`${formatHours(item.row.actual_hours)} / ${formatHours(item.row.current_plan_hours)}h`}
            ariaLabel="Service type meter"
          />
        ),
      },
    ],
    [],
  );

  return (
    <section className={styles.performanceSection}>
      <header className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Service Type Breakdown</h3>
        <p className={styles.sectionSubtitle}>
          Rolled up from current plan and actuals across the job.
        </p>
      </header>

      <InfiniteTable<ServiceTypeTableRow>
        tableId="job-performance-service-type-breakdown"
        columns={columns}
        rows={tableRows}
        emptyMessage="No service type data for this scope."
        hasMore={false}
        isLoading={false}
        getRowId={(row) => row.id}
        getRowSortPosition={(row) =>
          row.kind === 'totals' ? 'last' : 'normal'
        }
        rowHeight={70}
        virtualizedHeight="auto"
        onLoadMore={() => {}}
      />
    </section>
  );
}
